import { apiClient } from '../utils/api';

// ============================================
// TYPES
// ============================================

export interface ActivityType {
  id: string;
  companyId?: string;
  name: string;
  description?: string;
  defaultColor: string;
  isBlocking: boolean;
  isDefault?: boolean;
  defaultVisibility: 'INTERNAL' | 'FRONTOFFICE' | 'PRIVATE';
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeActivityType extends ActivityType {}

export interface PractitionerActivity {
  id: string;
  practitionerId?: string | null;
  employeeId?: string | null;
  shopId?: string | null;
  activityTypeId?: string | null;
  employeeActivityTypeId?: string | null;
  title: string;
  description?: string;
  colorOverride?: string;
  startDateTime: string;
  endDateTime: string;
  isAllDay: boolean;
  isBlockingOverride?: boolean | null;
  visibility: 'INTERNAL' | 'FRONTOFFICE' | 'PRIVATE';
  status: 'PLANNED' | 'COMPLETED' | 'CANCELLED';
  timezone: string;
  createdByAdminId?: string;
  createdAt: string;
  updatedAt: string;
  // Enriched fields from API
  activityTypeName?: string;
  activityTypeColor?: string;
  typeIsBlocking?: boolean;
  effectiveIsBlocking?: boolean;
  effectiveColor?: string;
  practitionerFirstName?: string;
  practitionerLastName?: string;
  employeeFirstName?: string;
  employeeLastName?: string;
  employeeDisplayName?: string;
  shopName?: string;
  shopTimezone?: string;
}

export interface CreateActivityTypeData {
  companyId?: string;
  name: string;
  description?: string;
  defaultColor?: string;
  isBlocking?: boolean;
  isDefault?: boolean;
  defaultVisibility?: 'INTERNAL' | 'FRONTOFFICE' | 'PRIVATE';
}

export interface UpdateActivityTypeData extends Partial<CreateActivityTypeData> {}

export interface CreateActivityData {
  practitionerId?: string | null;
  employeeId?: string | null;
  shopId?: string | null;
  activityTypeId: string;
  employeeActivityTypeId?: string | null;
  title: string;
  description?: string;
  colorOverride?: string | null;
  startDateTime: string; // ISO datetime string
  endDateTime: string; // ISO datetime string
  isAllDay?: boolean;
  isBlockingOverride?: boolean | null;
  visibility?: 'INTERNAL' | 'FRONTOFFICE' | 'PRIVATE';
  status?: 'PLANNED' | 'COMPLETED' | 'CANCELLED';
  timezone?: string;
}

export interface UpdateActivityData extends Partial<CreateActivityData> {}

export interface GetActivitiesParams {
  shopId: string;
  from: string; // ISO datetime
  to: string; // ISO datetime
  practitionerId?: string;
  employeeId?: string;
  activityTypeId?: string;
  employeeActivityTypeId?: string;
  status?: 'PLANNED' | 'COMPLETED' | 'CANCELLED';
}

export interface GetAllActivitiesParams {
  from: string; // ISO datetime
  to: string; // ISO datetime
  practitionerIds?: string[]; // Array of practitioner IDs
  employeeIds?: string[]; // Array of employee IDs
  shopIds?: string[]; // Array of shop IDs
  activityTypeId?: string;
  employeeActivityTypeId?: string;
  status?: 'PLANNED' | 'COMPLETED' | 'CANCELLED';
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

// ============================================
// ACTIVITY TYPE API FUNCTIONS
// ============================================

/**
 * Get all activity types
 * @param companyId - Optional company ID filter
 */
export async function getActivityTypes(companyId?: string): Promise<ActivityType[]> {
  const params = new URLSearchParams();
  if (companyId) {
    params.append('companyId', companyId);
  }

  const response = await apiClient(`/api/planning/activity-types?${params}`);
  
  if (!response.ok) {
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Accès refusé aux types d\'activité praticien.');
    }
    throw new Error('Failed to fetch activity types');
  }

  const data: ApiResponse<{ activityTypes: ActivityType[] }> = await response.json();
  
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch activity types');
  }

  return data.data?.activityTypes || [];
}

/**
 * Get a single activity type by ID
 */
export async function getActivityType(id: string): Promise<ActivityType> {
  const types = await getActivityTypes();
  const type = types.find(t => t.id === id);
  
  if (!type) {
    throw new Error('Activity type not found');
  }

  return type;
}

/**
 * Create a new activity type
 */
export async function createActivityType(data: CreateActivityTypeData): Promise<string> {
  const response = await apiClient('/api/planning/activity-types', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to create activity type');
  }

  const result: ApiResponse<{ activityTypeId: string }> = await response.json();
  
  if (!result.success) {
    throw new Error(result.message || 'Failed to create activity type');
  }

  return result.data?.activityTypeId || '';
}

/**
 * Update an activity type
 */
export async function updateActivityType(id: string, data: UpdateActivityTypeData): Promise<void> {
  const response = await apiClient(`/api/planning/activity-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update activity type');
  }

  const result: ApiResponse<void> = await response.json();
  
  if (!result.success) {
    throw new Error(result.message || 'Failed to update activity type');
  }
}

/**
 * Delete an activity type
 */
export async function deleteActivityType(id: string): Promise<void> {
  const response = await apiClient(`/api/planning/activity-types/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete activity type');
  }

  const result: ApiResponse<void> = await response.json();
  
  if (!result.success) {
    throw new Error(result.message || 'Failed to delete activity type');
  }
}

// ============================================
// EMPLOYEE ACTIVITY TYPE API FUNCTIONS
// ============================================

export async function getEmployeeActivityTypes(companyId?: string): Promise<EmployeeActivityType[]> {
  const params = new URLSearchParams();
  if (companyId) {
    params.append('companyId', companyId);
  }

  const response = await apiClient(`/api/planning/employee-activity-types?${params}`);

  if (!response.ok) {
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Accès refusé aux types d\'activité employé.');
    }
    throw new Error('Failed to fetch employee activity types');
  }

  const data: ApiResponse<{ activityTypes: EmployeeActivityType[] }> = await response.json();

  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch employee activity types');
  }

  return data.data?.activityTypes || [];
}

export async function getEmployeeActivityType(id: string): Promise<EmployeeActivityType> {
  const types = await getEmployeeActivityTypes();
  const type = types.find((t) => t.id === id);

  if (!type) {
    throw new Error('Employee activity type not found');
  }

  return type;
}

export async function createEmployeeActivityType(data: CreateActivityTypeData): Promise<string> {
  const response = await apiClient('/api/planning/employee-activity-types', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to create employee activity type');
  }

  const result: ApiResponse<{ activityTypeId: string }> = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to create employee activity type');
  }

  return result.data?.activityTypeId || '';
}

export async function updateEmployeeActivityType(id: string, data: UpdateActivityTypeData): Promise<void> {
  const response = await apiClient(`/api/planning/employee-activity-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update employee activity type');
  }

  const result: ApiResponse<void> = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to update employee activity type');
  }
}

export async function deleteEmployeeActivityType(id: string): Promise<void> {
  const response = await apiClient(`/api/planning/employee-activity-types/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete employee activity type');
  }

  const result: ApiResponse<void> = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to delete employee activity type');
  }
}

// ============================================
// PRACTITIONER ACTIVITY API FUNCTIONS
// ============================================

/**
 * Get activities for a shop
 */
export async function getShopActivities(params: GetActivitiesParams): Promise<PractitionerActivity[]> {
  const queryParams = new URLSearchParams({
    from: params.from,
    to: params.to,
  });

  if (params.practitionerId) {
    queryParams.append('practitionerId', params.practitionerId);
  }
  if (params.employeeId) {
    queryParams.append('employeeId', params.employeeId);
  }
  if (params.activityTypeId) {
    queryParams.append('activityTypeId', params.activityTypeId);
  }
  if (params.employeeActivityTypeId) {
    queryParams.append('employeeActivityTypeId', params.employeeActivityTypeId);
  }
  if (params.status) {
    queryParams.append('status', params.status);
  }

  const response = await apiClient(`/api/planning/activities/shop/${params.shopId}?${queryParams}`);
  
  if (!response.ok) {
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Vous n\'avez pas accès à cette boutique. Veuillez sélectionner une boutique à laquelle vous avez accès.');
    }
    throw new Error('Failed to fetch activities');
  }

  const data: ApiResponse<{ activities: PractitionerActivity[] }> = await response.json();
  
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch activities');
  }

  return data.data?.activities || [];
}

/**
 * Get all activities with optional filters (practitioner, shop)
 * This is the main function for calendar views - loads all activities by default
 */
export async function getAllActivities(params: GetAllActivitiesParams): Promise<PractitionerActivity[]> {
  const queryParams = new URLSearchParams({
    from: params.from,
    to: params.to,
  });

  // Add practitioner filter if provided
  if (params.practitionerIds && params.practitionerIds.length > 0) {
    queryParams.append('practitionerIds', params.practitionerIds.join(','));
  }

  // Add employee filter if provided
  if (params.employeeIds && params.employeeIds.length > 0) {
    queryParams.append('employeeIds', params.employeeIds.join(','));
  }

  // Add shop filter if provided
  if (params.shopIds && params.shopIds.length > 0) {
    queryParams.append('shopIds', params.shopIds.join(','));
  }

  if (params.activityTypeId) {
    queryParams.append('activityTypeId', params.activityTypeId);
  }
  if (params.employeeActivityTypeId) {
    queryParams.append('employeeActivityTypeId', params.employeeActivityTypeId);
  }

  if (params.status) {
    queryParams.append('status', params.status);
  }

  const response = await apiClient(`/api/planning/activities?${queryParams}`);
  
  if (!response.ok) {
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          'Vous n\'avez pas accès au planning pour cette boutique. Vérifiez votre sélection de shop.',
      );
    }
    throw new Error('Failed to fetch activities');
  }

  const data: ApiResponse<{ activities: PractitionerActivity[] }> = await response.json();
  
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch activities');
  }

  return data.data?.activities || [];
}

/**
 * Get a single activity by ID
 * Note: This requires fetching activities for the shop and finding by ID
 */
export async function getActivity(id: string, shopId: string, from: string, to: string): Promise<PractitionerActivity> {
  const activities = await getShopActivities({ shopId, from, to });
  const activity = activities.find(a => a.id === id);
  
  if (!activity) {
    throw new Error('Activity not found');
  }

  return activity;
}

/**
 * Create a new practitioner activity
 */
export async function createActivity(data: CreateActivityData): Promise<string> {
  const response = await apiClient('/api/planning/activities', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to create activity');
  }

  const result: ApiResponse<{ activityId: string }> = await response.json();
  
  if (!result.success) {
    throw new Error(result.message || 'Failed to create activity');
  }

  return result.data?.activityId || '';
}

/**
 * Update a practitioner activity
 */
export async function updateActivity(
  id: string,
  data: UpdateActivityData,
  options?: { skipGlobalLoading?: boolean }
): Promise<void> {
  const response = await apiClient(`/api/planning/activities/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    headers: options?.skipGlobalLoading ? { 'X-Skip-Global-Loading': 'true' } : undefined,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update activity');
  }

  const result: ApiResponse<void> = await response.json();
  
  if (!result.success) {
    throw new Error(result.message || 'Failed to update activity');
  }
}

/**
 * Delete a practitioner activity
 */
export async function deleteActivity(id: string): Promise<void> {
  const response = await apiClient(`/api/planning/activities/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete activity');
  }

  const result: ApiResponse<void> = await response.json();
  
  if (!result.success) {
    throw new Error(result.message || 'Failed to delete activity');
  }
}

